import * as React from 'react';

import type {Lead as LeadFragment} from 'nutshell-graphql-types';

import {CardWidgetWrapper} from '../../../core/card/card-widget-wrapper';
import {MessageBodyPreviewModal} from '../../../email-marketing/drip-sequences/recipient-modal/message-body-preview-modal';
import {EntityPageEmailSequencesSection} from '../../../peep/email-marketing/email-sequences/entity-page-email-sequences-section';

import {PipelineTasks} from '../../content/sales-process-widget/old-sales-process/pipeline-tasks';
import {LeadReactContainer} from '../../deprecated/lead-react-container';
import {
    useGetLeadDripSequenceProgressions,
    useCancelDripProgression,
} from '../../shared/graphql/hooks';

import {LeadChangePipeline} from '../stages/lead-change-pipeline';

import {DripSequences} from './components/drip-sequences';
import {getPipelineWidgetSecondaryExpandLabel} from './helpers';

type Props = {
    lead: LeadFragment;
    pipelineTasks: HTMLElement | null | undefined;
    pipelineTasksCount: number;
    onRenderLeadMarkup: () => void;
};

export function PipelineWidget(props: Props) {
    const [editionToShow, setEditionToShow] = React.useState<
        | {
              templateId: string;
              entityId: string;
          }
        | null
        | undefined
    >(undefined);
    // Matches the state within LeadReactContainer
    const [temporarilySelectedStageId, setTemporarilySelectedStageId] = React.useState<
        string | null | undefined
    >(null);

    const {emailSequences, refetch} = useGetLeadDripSequenceProgressions(props.lead.id);
    const {cancelDripProgression} = useCancelDripProgression();

    const numStageTasks = props.pipelineTasksCount;

    // Stage selection handlers
    const handleTemporarilySelectStage = React.useCallback((stageId: string) => {
        setTemporarilySelectedStageId(stageId);
    }, []);

    const handleClearTemporaryStageSelection = React.useCallback(() => {
        setTemporarilySelectedStageId(null);
    }, []);

    const tempStageProps = React.useMemo(() => {
        return {
            temporarilySelectedStageId,
            onTemporarilySelectStage: handleTemporarilySelectStage,
            onClearTemporaryStageSelection: handleClearTemporaryStageSelection,
        };
    }, [
        temporarilySelectedStageId,
        handleTemporarilySelectStage,
        handleClearTemporaryStageSelection,
    ]);

    const secondaryExpandLabel = getPipelineWidgetSecondaryExpandLabel(numStageTasks);

    return (
        <>
            <CardWidgetWrapper
                widgetKey='leadPipeline'
                collapsedContent={
                    <LeadReactContainer
                        key='collapsed'
                        id={props.lead.id}
                        isWidget={true}
                        hideNonCarouselContent={true}
                        tempStage={tempStageProps}
                    />
                }
                secondaryTitle={
                    props.lead.stageset ? (
                        <LeadChangePipeline
                            leadId={props.lead.id}
                            stageset={props.lead.stageset}
                            onlyShowButton={true}
                        />
                    ) : null
                }
                secondaryExpandProps={{
                    text: secondaryExpandLabel,
                }}
                rowsHavePadding={false}
            >
                <div className='mb-24'>
                    <LeadReactContainer
                        key='full'
                        id={props.lead.id}
                        isWidget={true}
                        tempStage={tempStageProps}
                    />
                </div>
                <DripSequences
                    emailSequences={emailSequences}
                    entityType='lead'
                    showPreviewModal={(emailTemplateId, entityId) => {
                        setEditionToShow({templateId: emailTemplateId, entityId: entityId});
                    }}
                    onCancelSequenceProgression={(dripProgressionId: string) =>
                        cancelDripProgression(dripProgressionId).then(() => refetch())
                    }
                />
                <EntityPageEmailSequencesSection entityId={props.lead.id} entityType='lead' />
                {props.pipelineTasks && !NutClientConfig.hasLeadPageRedesignM3FF && (
                    <PipelineTasks
                        pipelineTasks={props.pipelineTasks}
                        numTasks={numStageTasks}
                        onRenderLeadMarkup={props.onRenderLeadMarkup}
                    />
                )}
            </CardWidgetWrapper>
            {editionToShow && (
                <MessageBodyPreviewModal
                    onClose={() => {
                        setEditionToShow(undefined);
                    }}
                    emailTemplateId={editionToShow.templateId}
                    entityId={editionToShow.entityId}
                />
            )}
        </>
    );
}
